import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { AppConstants } from '../global';
import { GeocodeResponse } from '../interfaces/geocode-response.interface';
import { Order } from '../interfaces/order.interface';
import { Timeslots } from '../interfaces/timeslots.interface';
import { OrderStore, TrackingLocation } from '../order-store';
import { MessageDialogService } from './message-dialog.service';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(OrderStore);
  private readonly router = inject(Router);
  private readonly messageDialogService = inject(MessageDialogService);

  private readonly googleMapKey = 'AIzaSyDvDPPTr95S31flvvJcDTZJklsyEuyuomU';

  public getOrderInfo(): void {
    const urlParams = this.store.urlParams();
    if (!urlParams) {
      return;
    }
    this.showLoader();
    const httpOptions = {
      headers: new HttpHeaders({ 'X-Order-Story-Version': 'v2' }),
    };
    this.http
      .get<Order>(
        `${AppConstants.get('API')}/${urlParams.oid}/${urlParams.routeType}?key=${urlParams.key}`,
        httpOptions,
      )
      .subscribe((res) => {
        this.messageDialogService.hideMessage();
        if (res) {
          this.store.setConfirmedData({
            time: res.estimatedTime,
            address: res.address,
            location: {
              latitude: Number(res.location.latitude),
              longitude: Number(res.location.longitude),
            },
          });
          this.store.setOrder(res);
          this.hideLoader();
          this.router.navigate(['main'], { queryParamsHandling: 'merge' });
        }
      });
  }

  public confirmData(): void {
    const urlParams = this.store.urlParams();
    const confirmedData = this.store.confirmedData();
    if (!urlParams || !confirmedData) {
      return;
    }
    this.showLoader();
    const data: Record<string, unknown> = {
      ...confirmedData,
      key: urlParams.key,
    };
    if (!data['addressToSave']) {
      delete data['addressToSave'];
    }
    this.http
      .post<Partial<Order>>(
        `${AppConstants.get('API')}/${urlParams.oid}/${urlParams.routeType}/confirm`,
        data,
      )
      .subscribe((res) => {
        this.hideLoader();
        if (res && res.address) {
          this.store.setOrder(res as Order);
        } else {
          this.getOrderInfo();
        }
      });
  }

  public getTimeslots(): void {
    const urlParams = this.store.urlParams();
    if (!urlParams) {
      return;
    }
    this.showLoader();
    this.http
      .get<Timeslots>(
        `${AppConstants.get('API')}/${urlParams.oid}/${urlParams.routeType}/slots?key=${urlParams.key}`,
      )
      .subscribe((res) => {
        this.hideLoader();
        this.store.setTiming(res);
      });
  }

  public getOrderTrackingLocation(): void {
    const urlParams = this.store.urlParams();
    if (!urlParams) {
      return;
    }
    this.http
      .get<TrackingLocation>(
        `${AppConstants.get('API')}/${urlParams.oid}/location?key=${urlParams.key}`,
      )
      .subscribe((res) => {
        this.store.setTrackingLocation(res);
      });
  }

  public getGeodedInfo(lat: number, lng: number) {
    return this.http.get<GeocodeResponse>(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${this.googleMapKey}`,
    );
  }

  public getEstimationTime(
    dropOffLocation: { latitude: string | number; longitude: string | number },
    packageLocation: { latitude: string | number; longitude: string | number },
  ) {
    return this.http.get(
      `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${dropOffLocation.latitude},${dropOffLocation.longitude}&destinations=${packageLocation.latitude},${packageLocation.longitude}&key=${this.googleMapKey}`,
    );
  }

  private showLoader(): void {
    this.messageDialogService.showMessage({ type: 'loader' });
  }

  private hideLoader(): void {
    this.messageDialogService.hideMessage();
  }
}
